import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Coins, Sparkles, Star, ArrowRight, Loader2, CheckCircle2, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const ORDERS_PAGE_SIZE = 10;

export default function AccountCredits() {
  const [plans, setPlans] = useState([]);
  const [balance, setBalance] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loadingPkg, setLoadingPkg] = useState(null);
  const [isCommercialista, setIsCommercialista] = useState(false);
  const [ordersPage, setOrdersPage] = useState(0);

  const load = async () => {
    const [p, b, o] = await Promise.all([
      api.get("/pricing/plans"),
      api.get("/credits/balance"),
      api.get("/orders"),
    ]);
    setPlans(p.data.packages);
    setIsCommercialista(!!p.data.is_commercialista);
    setBalance(b.data);
    setOrders(o.data);
  };
  useEffect(() => { load(); }, []);

  const buy = async (pkg) => {
    setLoadingPkg(pkg.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile avviare il pagamento");
    } finally {
      setLoadingPkg(null);
    }
  };

  return (
    <div data-testid="account-credits">
      {/* Balance card */}
      <div className="rounded-2xl border border-[#E8E4D9] bg-gradient-to-br from-[#0A0A0A] to-[#1D4ED8] text-white p-6 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">Crediti disponibili</div>
          <div className="font-display font-extrabold text-5xl tracking-tighter mt-1 tabular-nums" data-testid="credits-balance">
            {balance?.credits_available ?? "—"}
          </div>
          {balance?.pending_invoices > 0 && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs bg-amber-500/20 text-amber-100 border border-amber-300/30 px-2.5 py-1 rounded-full">
              <Sparkles className="w-3 h-3" /> {balance.pending_invoices} in coda — servono crediti
            </div>
          )}
        </div>
        <Coins className="w-16 h-16 text-white/20" />
      </div>

      {/* Plans */}
      <div className="flex items-center justify-between mt-10 mb-4 flex-wrap gap-2">
        <h2 className="font-display font-bold text-2xl tracking-tight">Pacchetti · pagamento una tantum</h2>
        {isCommercialista && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#B8985C]/15 text-[#8b6f3f] text-[11px] font-bold uppercase tracking-wider" data-testid="studio-pricing-badge">
            <Star className="w-3 h-3" /> Prezzo studio · fino al 17% in meno
          </span>
        )}
      </div>
      <p className="text-sm text-[#4A4A4A] mb-6">
        1 credito = 1 fattura elaborata dall&apos;AI. I crediti non scadono. Servizio disponibile solo per utenti fiscalmente italiani.
        {isCommercialista && " Come commercialista approvato hai accesso ai piani Studio con sconto dedicato."}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {plans.map((p) => (
          <div key={p.id} data-testid={`plan-${p.id}`} className={`rounded-2xl border p-5 flex flex-col ${p.featured ? "border-[#1D4ED8] bg-blue-50/40 ring-2 ring-[#1D4ED8]/20" : "border-[#E8E4D9] bg-white"}`}>
            {p.featured && <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#1D4ED8] font-bold mb-2 self-start">
              <Star className="w-3 h-3" /> Più popolare
            </div>}
            <div className="font-display font-extrabold text-2xl tracking-tight">{p.name}</div>
            <div className="text-xs text-[#737373]">{p.tagline}</div>
            <div className="font-display font-extrabold text-4xl tracking-tighter mt-4">
              € {p.amount_eur.toFixed(2).replace(".", ",")}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[#737373] mt-1">
              IVA 22% inclusa
            </div>
            <div className="text-xs text-[#737373] mt-2"><strong className="text-[#0A0A0A]">{p.credits}</strong> crediti · €{p.per_invoice_eur.toFixed(2).replace(".", ",")}/fattura</div>
            <button
              onClick={() => buy(p)}
              disabled={loadingPkg === p.id}
              data-testid={`buy-${p.id}`}
              className={`mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm disabled:opacity-60 ${
                p.featured ? "bg-[#1D4ED8] text-white hover:bg-[#1D4ED8]/90" : "bg-[#0A0A0A] text-white hover:bg-[#1D4ED8]"
              }`}
            >
              {loadingPkg === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Acquista <ArrowRight className="w-3.5 h-3.5" /></>}
            </button>
          </div>
        ))}
      </div>

      {/* Order history */}
      <h2 className="font-display font-bold text-2xl mt-12 mb-4 tracking-tight">Storico ordini</h2>
      {orders.length === 0 ? (
        <p className="text-sm text-[#737373] italic">Nessun ordine ancora.</p>
      ) : (
        <OrdersTable orders={orders} page={ordersPage} setPage={setOrdersPage} />
      )}
    </div>
  );
}

function OrdersTable({ orders, page, setPage }) {
  const totalPages = Math.max(1, Math.ceil(orders.length / ORDERS_PAGE_SIZE));
  const pageStart = page * ORDERS_PAGE_SIZE;
  const paged = useMemo(
    () => orders.slice(pageStart, pageStart + ORDERS_PAGE_SIZE),
    [orders, pageStart]
  );
  return (
    <div className="rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden" data-testid="orders-table-wrap">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#F5F2EA] border-b border-[#E8E4D9] text-[10px] uppercase tracking-[0.15em] text-[#737373] font-bold">
            <th className="text-left px-4 py-2.5">Data</th>
            <th className="text-left px-4 py-2.5">Pacchetto</th>
            <th className="text-left px-4 py-2.5 w-24">Crediti</th>
            <th className="text-left px-4 py-2.5 w-28">Importo</th>
            <th className="text-left px-4 py-2.5 w-32">Stato</th>
            <th className="text-left px-4 py-2.5 w-32">Proforma</th>
          </tr>
        </thead>
        <tbody>
          {paged.map((o, i) => (
            <tr key={o.session_id} className={i > 0 ? "border-t border-[#E8E4D9]/70" : ""} data-testid={`order-row-${pageStart + i}`}>
              <td className="px-4 py-2.5 font-mono text-xs">{String(o.created_at).slice(0, 19).replace("T", " ")}</td>
              <td className="px-4 py-2.5 font-medium">{o.package_id}</td>
              <td className="px-4 py-2.5 tabular-nums">{o.credits}</td>
              <td className="px-4 py-2.5 tabular-nums">€ {Number(o.amount).toFixed(2).replace(".", ",")}</td>
              <td className="px-4 py-2.5">
                {o.payment_status === "paid" ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded uppercase tracking-wider">
                    <CheckCircle2 className="w-3 h-3" /> Pagato
                  </span>
                ) : (
                  <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded uppercase tracking-wider">
                    {o.payment_status}
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5">
                {o.payment_status === "paid" ? (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.session_id}/proforma`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`download-proforma-${pageStart + i}`}
                    className="inline-flex items-center gap-1 text-[10px] font-bold text-[#1D4ED8] hover:underline"
                  >
                    <Download className="w-3 h-3" /> {o.proforma_number || "Scarica PDF"}
                  </a>
                ) : (
                  <span className="text-[10px] text-[#A3A3A3]">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div
          className="border-t border-[#E8E4D9] px-4 py-3 flex items-center justify-between text-sm bg-[#FAF9F6]"
          data-testid="orders-pagination"
        >
          <div className="text-[#737373]">
            Pagina <span className="font-semibold text-[#0A0A0A]">{page + 1}</span> di{" "}
            <span className="font-semibold text-[#0A0A0A]">{totalPages}</span>
            <span className="hidden sm:inline text-[#A3A3A3] ml-2">
              ({pageStart + 1}–{Math.min(pageStart + ORDERS_PAGE_SIZE, orders.length)} su {orders.length})
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              data-testid="orders-prev-page"
              className="inline-flex items-center gap-1 h-8 pl-2 pr-3 rounded-full border border-[#E8E4D9] bg-white text-[#0A0A0A] hover:bg-[#F5F2EA] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Precedente
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              data-testid="orders-next-page"
              className="inline-flex items-center gap-1 h-8 pl-3 pr-2 rounded-full border border-[#E8E4D9] bg-white text-[#0A0A0A] hover:bg-[#F5F2EA] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Successiva <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
